import { breedNameWithoutAlias } from '../formatters/string';

export const BREED_HANDBOOKS_FLAG = 'breed_handbooks';
export const BREED_HANDBOOKS_ADMIN_ONLY_FLAG = 'breed_handbooks.admin_only';
export const BREED_HANDBOOKS_EARLY_READERS_FLAG =
  'breed_handbooks.early_readers';
export const BREED_HANDBOOKS_WAITLIST_FOLLOWUP_FLAG =
  'breed_handbooks.waitlist_followup_email';
export const WAITLIST_FOLLOWUP_EMAIL_CASE_KEY =
  'email.breedHandbooks.waitlistFollowup';
export const WAITLIST_FOLLOWUP_DEFAULT_DELAY_MINUTES = 20 * 60;
export const WAITLIST_FOLLOWUP_MIN_DELAY_MINUTES = 10;
export const WAITLIST_FOLLOWUP_MAX_DELAY_MINUTES = 14 * 24 * 60;
export const WAITLIST_FOLLOWUP_SEND_WINDOW_MINUTES = 28 * 60;

export const isValidWaitlistFollowupDelay = (minutes: number): boolean =>
  Number.isInteger(minutes) &&
  minutes >= WAITLIST_FOLLOWUP_MIN_DELAY_MINUTES &&
  minutes <= WAITLIST_FOLLOWUP_MAX_DELAY_MINUTES;

export const BREED_HANDBOOK_PRICE_CENTS = 1199;
export const BREED_HANDBOOK_RETURNING_PRICE_CENTS = 899;
export const BREED_HANDBOOK_PRICES_CENTS = [
  BREED_HANDBOOK_PRICE_CENTS,
  BREED_HANDBOOK_RETURNING_PRICE_CENTS
] as const;

export const BREED_HANDBOOK_EARLY_READER_LIMIT = 25;

export const BREED_HANDBOOK_RELAUNCH_DAY = '2026-09-23';

export const isAdditionalBreedPrice = (cents: number): boolean =>
  cents <= BREED_HANDBOOK_RETURNING_PRICE_CENTS;

export const BREED_HANDBOOK_CURRENCY = 'cad';
export const BREED_HANDBOOK_PRODUCT_KEY = 'breed_handbook';

export const BREED_HANDBOOKS_PATH = '/purchaser/handbooks';

export const BREED_GUIDES_PATH = '/dog-breed-guides';

export const BREED_HANDBOOK_PLACEHOLDER_PHOTO_URL =
  'https://paction-shared-resources.s3.ca-central-1.amazonaws.com/breedPhotos/XG0HciTpVp.webp';

export const formatBreedHandbookPrice = (
  cents: number = BREED_HANDBOOK_PRICE_CENTS
): string => `${(cents / 100).toFixed(2)} CAD`;

export const breedHandbookPath = (breedSlug: string): string =>
  `/dog-breeds/${encodeURIComponent(breedSlug)}/handbook`;

export const BREED_HANDBOOK_FIT_CHECK_ANCHOR = 'fit-check';

export const breedHandbookFitCheckPath = (breedSlug: string): string =>
  `${breedHandbookPath(breedSlug)}#${BREED_HANDBOOK_FIT_CHECK_ANCHOR}`;

export const BREED_HANDBOOK_FIT_CHECK_CTA = 'Try the free fit check';

export const myBreedHandbookPath = (breedSlug: string): string =>
  `${BREED_HANDBOOKS_PATH}/${encodeURIComponent(breedSlug)}`;

export const BREED_HANDBOOK_QUESTIONS_ANCHOR = 'questions';

export const myBreedHandbookQuestionsPath = (breedSlug: string): string =>
  `${myBreedHandbookPath(breedSlug)}#${BREED_HANDBOOK_QUESTIONS_ANCHOR}`;

export const breedHandbookTitle = (breedName: string | null): string =>
  breedName
    ? `Choosing a Healthy ${breedNameWithoutAlias(breedName)}`
    : 'Choosing a Healthy Puppy';

export const breedHandbookPersonalizedFor = (
  firstName?: string | null
): string => {
  const name = firstName?.trim();
  return name
    ? `Personalized for ${name}'s home`
    : 'Personalized for your home';
};

export const breedHandbookProductName = (breedName: string | null): string =>
  `${breedHandbookTitle(breedName)} (personalized guide)`;

export const breedHandbookMetaDescription = (
  breedName: string,
  screeningCount: number
): string =>
  `${breedNameWithoutAlias(breedName)} health issues and the ${screeningCount} ${screeningCount === 1 ? 'health test' : 'health tests'} to ask a breeder about, explained, with an honest breed match and questions for your home.`;

export const HOUSEHOLD_OPTIONS = [
  { value: 'alone', label: 'Just me' },
  { value: 'partner', label: 'A partner' },
  { value: 'roommates', label: 'Roommates' },
  { value: 'familyFiveOrLess', label: 'Children 5 or under' },
  { value: 'familyGreaterThanFive', label: 'Children over 5' }
] as const;

export const HOME_TYPE_OPTIONS = [
  { value: 'condo', label: 'Condo or apartment' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'house', label: 'House' }
] as const;

export const OUTDOOR_SPACE_OPTIONS = [
  { value: 'fencedYard', label: 'Fenced yard' },
  { value: 'unfencedYard', label: 'Unfenced yard' },
  { value: 'dogPark', label: 'Dog park nearby' },
  { value: 'outdoorSpace', label: 'Other outdoor space' }
] as const;

export const OTHER_PETS_OPTIONS = [
  { value: 'none', label: 'No other pets' },
  { value: 'dogs', label: 'Dogs' },
  { value: 'cats', label: 'Cats' },
  { value: 'other', label: 'Other animals' }
] as const;

export const DAILY_TIME_OPTIONS = [
  { value: 'under30', label: 'Under 30 minutes' },
  { value: '30to60', label: '30 to 60 minutes' },
  { value: '60to120', label: '1 to 2 hours' },
  { value: 'over120', label: 'More than 2 hours' }
] as const;

export const EXPERIENCE_OPTIONS = [
  { value: 'firstDog', label: 'This will be my first dog' },
  { value: 'hadDogs', label: 'I have raised a dog before' }
] as const;

export const ALLERGY_OPTIONS = [
  { value: 'no', label: 'No allergies' },
  { value: 'yes', label: 'Someone has dog allergies' }
] as const;

export const HOURS_ALONE_OPTIONS = [
  { value: 'under2', label: 'Under 2 hours' },
  { value: '2to4', label: '2 to 4 hours' },
  { value: '4to6', label: '4 to 6 hours' },
  { value: '6to8', label: '6 to 8 hours' },
  { value: 'over8', label: 'More than 8 hours' }
] as const;

export const ACTIVITY_OPTIONS = [
  { value: 'walks', label: 'Mostly everyday walks' },
  { value: 'running', label: 'Running or jogging' },
  { value: 'hiking', label: 'Hiking' },
  { value: 'swimming', label: 'Swimming' },
  { value: 'sports', label: 'Dog sports like agility' }
] as const;

export const HANDBOOK_PROVINCE_OPTIONS = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' }
] as const;

export const HANDBOOK_MAX_PAST_BREEDS = 3;

export const HANDBOOK_MAX_BONUS_COMPARISONS = 3;

export const formatNameList = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

type OptionValue<T extends readonly { value: string }[]> = T[number]['value'];

export type HandbookProvinceCode = OptionValue<
  typeof HANDBOOK_PROVINCE_OPTIONS
>;

export type HandbookPersonalization = {
  household: OptionValue<typeof HOUSEHOLD_OPTIONS>[];
  homeType: OptionValue<typeof HOME_TYPE_OPTIONS> | null;
  outdoorSpace: OptionValue<typeof OUTDOOR_SPACE_OPTIONS>[];
  otherPets: OptionValue<typeof OTHER_PETS_OPTIONS>[];
  dailyTime: OptionValue<typeof DAILY_TIME_OPTIONS> | null;
  experience: OptionValue<typeof EXPERIENCE_OPTIONS> | null;
  allergies: OptionValue<typeof ALLERGY_OPTIONS> | null;
  province: HandbookProvinceCode | null;
  hoursAlone: OptionValue<typeof HOURS_ALONE_OPTIONS> | null;
  activities: OptionValue<typeof ACTIVITY_OPTIONS>[];
  pastBreeds: string[];
};

export const EMPTY_HANDBOOK_PERSONALIZATION: HandbookPersonalization = {
  household: [],
  homeType: null,
  outdoorSpace: [],
  otherPets: [],
  dailyTime: null,
  experience: null,
  allergies: null,
  province: null,
  hoursAlone: null,
  activities: [],
  pastBreeds: []
};

export type HandbookListAnswerKey =
  | 'household'
  | 'outdoorSpace'
  | 'otherPets'
  | 'activities';

const HANDBOOK_ANSWER_CONFLICTS: Record<
  HandbookListAnswerKey,
  { solo: readonly string[]; rivals: readonly (readonly string[])[] }
> = {
  household: { solo: ['alone'], rivals: [] },
  outdoorSpace: { solo: [], rivals: [['fencedYard', 'unfencedYard']] },
  otherPets: { solo: ['none'], rivals: [] },
  activities: { solo: ['walks'], rivals: [] }
};

export const toggleHandbookListAnswer = <T extends string>(
  key: HandbookListAnswerKey,
  current: readonly T[],
  value: T
): T[] => {
  if (current.includes(value)) {
    return current.filter((item) => item !== value);
  }
  const { solo, rivals } = HANDBOOK_ANSWER_CONFLICTS[key];
  if (solo.includes(value)) return [value];
  const rivalsOfValue = new Set(
    rivals.filter((group) => group.includes(value)).flat()
  );
  return [
    ...current.filter(
      (item) => !solo.includes(item) && !rivalsOfValue.has(item)
    ),
    value
  ];
};

export const normalizeHandbookListAnswer = <T extends string>(
  key: HandbookListAnswerKey,
  values: readonly T[]
): T[] => {
  const { solo, rivals } = HANDBOOK_ANSWER_CONFLICTS[key];
  const specific = values.filter((item) => !solo.includes(item));
  let result =
    specific.length > 0
      ? specific
      : values.filter((item) => solo.includes(item)).slice(0, 1);
  for (const group of rivals) {
    const kept = result.find((item) => group.includes(item));
    result = result.filter((item) => !group.includes(item) || item === kept);
  }
  return result;
};

export const normalizeHandbookPersonalization = <
  T extends Pick<HandbookPersonalization, HandbookListAnswerKey>
>(
  value: T
): T => ({
  ...value,
  household: normalizeHandbookListAnswer('household', value.household),
  outdoorSpace: normalizeHandbookListAnswer('outdoorSpace', value.outdoorSpace),
  otherPets: normalizeHandbookListAnswer('otherPets', value.otherPets),
  activities: normalizeHandbookListAnswer('activities', value.activities)
});

export const isHandbookHomeProfileComplete = (
  value: HandbookPersonalization
): boolean =>
  value.household.length > 0 &&
  value.homeType !== null &&
  value.otherPets.length > 0 &&
  value.dailyTime !== null &&
  value.experience !== null &&
  value.allergies !== null;

export const isHandbookPersonalizationComplete = (
  value: HandbookPersonalization
): boolean =>
  isHandbookHomeProfileComplete(value) &&
  value.province !== null &&
  value.hoursAlone !== null &&
  value.activities.length > 0;

const CANADIAN_POSTAL_CODE =
  /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])(\d[ABCEGHJ-NPRSTV-Z]\d)$/;

const POSTAL_CODE_PROVINCES: Record<string, HandbookProvinceCode[]> = {
  A: ['NL'],
  B: ['NS'],
  C: ['PE'],
  E: ['NB'],
  G: ['QC'],
  H: ['QC'],
  J: ['QC'],
  K: ['ON'],
  L: ['ON'],
  M: ['ON'],
  N: ['ON'],
  P: ['ON'],
  R: ['MB'],
  S: ['SK'],
  T: ['AB'],
  V: ['BC'],
  X: ['NT', 'NU'],
  Y: ['YT']
};

export const normalizeCanadianPostalCode = (
  value: string | null | undefined
): string | null => {
  const compact = (value ?? '').toUpperCase().replace(/[\s-]/g, '');
  const match = CANADIAN_POSTAL_CODE.exec(compact);
  return match ? `${match[1]} ${match[2]}` : null;
};

export const postalCodeMatchesProvince = (
  postalCode: string | null | undefined,
  province: HandbookProvinceCode
): boolean => {
  const normalized = normalizeCanadianPostalCode(postalCode);
  if (!normalized) return false;
  return (POSTAL_CODE_PROVINCES[normalized.charAt(0)] ?? []).includes(province);
};

const NUNAVUT_FORWARD_SORTATION_AREAS = ['X0A', 'X0B', 'X0C'];

export const handbookProvinceFromPostalCode = (
  postalCode: string | null | undefined
): HandbookProvinceCode | null => {
  const normalized = normalizeCanadianPostalCode(postalCode);
  if (!normalized) return null;
  const provinces = POSTAL_CODE_PROVINCES[normalized.charAt(0)] ?? [];
  if (provinces.length === 1) return provinces[0] ?? null;
  return NUNAVUT_FORWARD_SORTATION_AREAS.includes(normalized.slice(0, 3))
    ? 'NU'
    : 'NT';
};

export const handbookProvinceLabel = (
  province: HandbookProvinceCode | null
): string | null =>
  HANDBOOK_PROVINCE_OPTIONS.find((option) => option.value === province)
    ?.label ?? null;

export const handbookPostalCodeError = (
  postalCode: string | null | undefined
): string | null =>
  normalizeCanadianPostalCode(postalCode)
    ? null
    : 'Enter a Canadian postal code, like M5V 2T6.';
